/**
 * DetailSection - Notion-like compact section for detail pages
 * Use for displaying key-value data in a clean, scannable format
 */

import { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { detailStyles, propertyStyles } from '@/lib/styles';

interface DetailSectionProps {
  title?: string;
  children: ReactNode;
  className?: string;
  action?: ReactNode;
  noPadding?: boolean;
}

/**
 * Section container for detail pages
 */
export function DetailSection({ title, children, className, action, noPadding }: DetailSectionProps) {
  return (
    <div className={cn(detailStyles.section, className)}>
      {title && (
        <div className={detailStyles.sectionHeader}>
          <h3 className={detailStyles.sectionTitle}>{title}</h3>
          {action}
        </div>
      )}
      <div className={noPadding ? '' : detailStyles.sectionContent}>
        {children}
      </div>
    </div>
  );
}

interface PropertyRowProps {
  label: string;
  children: ReactNode;
  className?: string;
  compact?: boolean;
  icon?: ReactNode;
}

/**
 * Property row for key-value display
 */
export function PropertyRow({ label, children, className, compact, icon }: PropertyRowProps) {
  const isEmpty = children === null || children === undefined || children === '';

  return (
    <div className={cn(compact ? propertyStyles.rowCompact : propertyStyles.row, className)}>
      <dt className={compact ? propertyStyles.labelCompact : propertyStyles.label}>{label}</dt>
      <dd className={cn(
        icon ? propertyStyles.valueWithIcon : (isEmpty ? propertyStyles.valueEmpty : propertyStyles.value)
      )}>
        {icon && <span className="text-gray-400">{icon}</span>}
        {isEmpty ? '—' : children}
      </dd>
    </div>
  );
}

interface PropertyGroupProps {
  children: ReactNode;
  className?: string;
}

/**
 * Group multiple properties in a horizontal layout
 */
export function PropertyGroup({ children, className }: PropertyGroupProps) {
  return (
    <div className={cn(propertyStyles.group, className)}>
      {children}
    </div>
  );
}

interface InfoItemProps {
  icon?: ReactNode;
  label: string;
  value: ReactNode;
  className?: string;
}

/**
 * Compact inline info display (icon + label: value)
 */
export function InfoItem({ icon, label, value, className }: InfoItemProps) {
  return (
    <div className={cn('flex items-center gap-1.5 text-xs', className)}>
      {icon && <span className="text-gray-400">{icon}</span>}
      <span className="text-gray-500">{label}:</span>
      <span className="text-gray-900 font-medium">{value}</span>
    </div>
  );
}

interface MetaRowProps {
  children: ReactNode;
  className?: string;
}

/**
 * Row of metadata items (badges, dates, etc)
 */
export function MetaRow({ children, className }: MetaRowProps) {
  return (
    <div className={cn('flex items-center gap-3 flex-wrap text-xs', className)}>
      {children}
    </div>
  );
}
